import { useCallback, useEffect, useState } from "react";
import { IUserResponse } from "../types/user";
import ROLES, { IRole, ROLES_LIST } from "../constants/roles";

import { useAppDispatch } from "../hooks/redux";
import { openModal } from "../features/modals";

import _ from "lodash";
import axios from "axios";

import {
  Button,
  Stack,
  Chip,
  Avatar,
  Typography,
  Divider,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";

import { ClinicResponse } from "../types/clinic";
import DoneIcon from "@mui/icons-material/Done";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import userSchema from "../schemas/user";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { Delete } from "@mui/icons-material";
import MODALS from "../constants/modals";

interface IUserDetailsProps {
  user: IUserResponse;
  onChange: (user: IUserResponse) => void;
  onDeleted: () => void;
}

const UserDetails: React.FunctionComponent<IUserDetailsProps> = ({
  user,
  onChange,
  onDeleted,
}) => {
  const dispatch = useAppDispatch();

  const [isDeleting, setIsDeleting] = useState(false);
  const [userLocalCopy, setUserLocalCopy] = useState(user);
  const [clinics, setClinics] = useState<ClinicResponse[]>([]);

  useEffect(() => {
    setUserLocalCopy(user);
  }, [user]);

  const {
    values,
    handleChange,
    handleSubmit,
    isSubmitting,
    errors,
    submitCount,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      givenName: userLocalCopy.givenName,
      familyName: userLocalCopy.familyName,
      dateOfBirth: userLocalCopy.dateOfBirth,
    },
    validationSchema: userSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      axios
        .post(`/users/${user._id}`, values)
        .then((res) => {
          setUserLocalCopy(res.data);
          onChange(res.data);
        })
        .catch((err) => {
          console.log("err:", err);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    axios
      .get<ClinicResponse[]>("/clinics")
      .then((res) => {
        setClinics(res.data);
      })
      .catch((err) => {
        console.log("err:", err);
      });
  }, []);

  const handleDelete = useCallback(() => {
    setIsDeleting(true);
    axios
      .delete(`/users/${userLocalCopy._id}`)
      .then(() => {
        onDeleted();
      })
      .catch((err) => {
        console.error(err);
        setIsDeleting(false);
      });
  }, [userLocalCopy, onDeleted]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar alt={user.name} src={user.image} />
        <Stack flex={1} spacing={2}>
          <TextField
            id="givenName"
            label="Vārds"
            fullWidth
            value={values.givenName}
            onChange={handleChange}
            size="small"
            error={Boolean(errors.givenName) && submitCount > 0}
          />
          <TextField
            id="familyName"
            label="Uzvārds"
            fullWidth
            value={values.familyName}
            onChange={handleChange}
            size="small"
            error={Boolean(errors.familyName) && submitCount > 0}
          />
          <TextField
            id="name"
            label="E-pasts"
            fullWidth
            disabled
            value={userLocalCopy.email}
            size="small"
          />
          <DatePicker
            label="Dzimšanas datums"
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            value={moment(values.dateOfBirth)}
            onChange={(newValue) => {
              setFieldValue("dateOfBirth", newValue);
            }}
          />
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <LoadingButton
          startIcon={<Delete />}
          color="error"
          loading={isDeleting}
          onClick={() => {
            dispatch(
              openModal({
                type: MODALS.Approve,
                title: "Vai tiešām dzēst lietotāju?",
                message: "Lietotājam vairs nebūs piekļuve sistēmai",
                onApprove: () => {
                  handleDelete();
                },
              })
            );
          }}
        >
          Dzēst
        </LoadingButton>
        <LoadingButton
          loading={isSubmitting}
          onClick={() => handleSubmit()}
          variant="contained"
        >
          Saglabāt
        </LoadingButton>
      </Stack>
      <Divider />
      <Typography>Klīnikas</Typography>
      <div>
        <Grid container spacing={1}>
          {clinics.map((clinic) => {
            return (
              <Grid key={clinic._id} item>
                <ClinicChip
                  hasAccess={Boolean(
                    userLocalCopy.clinics &&
                      userLocalCopy.clinics
                        .map((clinic) => clinic._id)
                        .includes(clinic._id)
                  )}
                  clinic={clinic}
                  userId={user._id}
                  callback={(newUser) => {
                    setUserLocalCopy(newUser);
                    onChange(newUser);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </div>

      <Typography>Lomas</Typography>
      <div>
        <Grid container spacing={1}>
          {_.map(ROLES_LIST, (role: IRole, key: ROLES) => {
            return (
              <Grid key={key} item>
                <RoleChip
                  hasAccess={Boolean(
                    userLocalCopy.roles && userLocalCopy.roles.includes(key)
                  )}
                  role={key}
                  userId={user._id}
                  callback={(newUser) => {
                    setUserLocalCopy(newUser);
                    onChange(newUser);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </div>
    </Stack>
  );
};

interface IClinicChipProps {
  hasAccess: boolean;
  clinic: ClinicResponse;
  userId: string;
  callback: (user: IUserResponse) => void;
}

const ClinicChip: React.FunctionComponent<IClinicChipProps> = ({
  hasAccess,
  clinic,
  userId,
  callback,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const onClick = useCallback(() => {
    setIsLoading(true);
    if (hasAccess) {
      axios
        .delete<IUserResponse>(`/users/${userId}/clinics/${clinic._id}`)
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      axios
        .post<IUserResponse>(`/users/${userId}/clinics`, {
          clinicId: clinic._id,
        })
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [clinic._id, hasAccess, userId]);

  return (
    <Chip
      icon={
        isLoading ? (
          <CircularProgress color="inherit" size={20} />
        ) : hasAccess ? (
          <DoneIcon />
        ) : undefined
      }
      key={clinic._id}
      label={clinic.name}
      onClick={onClick}
      color={hasAccess ? "success" : undefined}
      variant={hasAccess ? undefined : "outlined"}
    />
  );
};

interface IRoleChipProps {
  hasAccess: boolean;
  role: ROLES;
  userId: string;
  callback: (user: IUserResponse) => void;
}

const RoleChip: React.FunctionComponent<IRoleChipProps> = ({
  hasAccess,
  role,
  userId,
  callback,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const onClick = useCallback(() => {
    setIsLoading(true);
    if (hasAccess) {
      axios
        .delete<IUserResponse>(`/users/${userId}/roles/${role}`)
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      axios
        .post<IUserResponse>(`/users/${userId}/roles`, { role })
        .then((response) => {
          callback(response.data);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [hasAccess, userId]);

  return (
    <Chip
      icon={
        isLoading ? (
          <CircularProgress color="inherit" size={20} />
        ) : hasAccess ? (
          <DoneIcon />
        ) : undefined
      }
      label={ROLES_LIST[role].name}
      onClick={onClick}
      color={hasAccess ? "success" : undefined}
      variant={hasAccess ? undefined : "outlined"}
    />
  );
};

export default UserDetails;
